import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import QRCode from "qrcode";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import BottomNav from "@/components/BottomNav";
import ShareBar from "@/components/ShareBar";
import OakLogo from "@/components/OakLogo";
import LoadingScreen from "@/components/LoadingScreen";
import InfraBadges, { INFRA_DISCLAIMER } from "@/components/InfraBadges";
import { api } from "@/lib/api";
import { useT } from "@/hooks/useT";
import { toast } from "sonner";
import { Trophy, Download, Image as ImageIcon, ArrowLeft, CheckCircle2, Copy, Award, Linkedin, Twitter } from "lucide-react";

export default function Certificate() {
  const { pathId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useT();
  const [cert, setCert] = useState(null);
  const [credits, setCredits] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    api.get(`/certificate/${pathId}`).then(({ data }) => setCert(data)).catch(() => toast.error("Could not load"));
    api.get("/credits/balance").then(({ data }) => setCredits(data.credits)).catch(() => {});
  }, [pathId]);

  // Generate the Open Badges 2.0 verification QR as a PNG data URL so it
  // bakes into the html-to-image PNG export and survives offline. Points
  // at the public verify route which 302s to the OB 2.0 assertion JSON.
  useEffect(() => {
    if (!cert?.cert_id) return;
    const verifyUrl = `${window.location.origin}/cert/${cert.cert_id}`;
    QRCode.toDataURL(verifyUrl, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 220,
      color: { dark: "#FFFFFF", light: "#00000000" },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(null));
  }, [cert?.cert_id]);

  const exportPng = async () => {
    if (!ref.current) return;
    setExporting(true);
    try {
      const dataUrl = await toPng(ref.current, { cacheBust: true, pixelRatio: 2, backgroundColor: "#0B0E14" });
      // Open in new tab — user can share via OS/web-share from there (stays in OAK ecosystem)
      const w = window.open();
      if (w) {
        w.document.write(`<img src='${dataUrl}' alt='OAK Certificate' style='max-width:100%'/>`);
        w.document.title = `Certificate · ${cert.name}`;
      } else {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `OAK_Certificate_${cert.cert_id}.png`;
        a.click();
      }
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!cert) return <LoadingScreen label="Issuing certificate…" />;

  // Two URLs:
  //   * verifyUrl — public React page, no JS-blocking redirects, what the
  //     learner lands on after a click. Used in the cert PNG/PDF QR area.
  //   * shareUrl — backend-served HTML at /api/og/cert/{certId} with full
  //     Open Graph metadata + a 50ms JS redirect to verifyUrl. We copy
  //     THIS one to clipboard so LinkedIn/Twitter/WhatsApp render rich
  //     previews when the learner pastes it.
  const apiOrigin = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
  const verifyUrl = typeof window !== "undefined" ? `${window.location.origin}/cert/${cert.cert_id}` : "";
  const shareUrl = cert.cert_id ? `${apiOrigin}/api/og/cert/${cert.cert_id}` : verifyUrl;
  const shareText = `I just earned an Oak AI Campus ${cert.mastery_level || "Mastery"} certificate in "${cert.goal}" with ${cert.equity}% Knowledge Equity. Master anything with OAK AI.`;

  const copyVerifyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Verify link copied — share with anyone");
    } catch {
      toast.error("Could not copy");
    }
  };

  // LinkedIn "Add to Profile" deep-link. Uses LinkedIn's public Add
  // Certification URL which pre-fills the form for any signed-in user.
  // No partner status needed. Source of truth for the verify URL is
  // shareUrl (server-rendered OG → soft-redirects to /cert/:id).
  const linkedInAddUrl = (() => {
    const issued = cert.issued_at ? new Date(cert.issued_at) : new Date();
    const params = new URLSearchParams({
      startTask: "CERTIFICATION_NAME",
      name: `Oak AI Campus — ${cert.mastery_level || "Mastery"} in ${cert.goal || ""}`.slice(0, 250),
      organizationName: "Oak AI Campus",
      issueYear: String(issued.getUTCFullYear()),
      issueMonth: String(issued.getUTCMonth() + 1),
      certUrl: shareUrl,
      certId: cert.cert_id || "",
    });
    return `https://www.linkedin.com/profile/add?${params.toString()}`;
  })();

  // X (Twitter) Web Intent — public, undocumented-but-stable, no
  // partner status. Pre-fills a tweet with the cert hashtag, the
  // mastery line, and the verifiable URL so any retweet leaks
  // attribution back to oakaiapp.com. The rendered cert image isn't
  // attached automatically (X intent doesn't accept media uploads),
  // but the verify URL includes OG meta so X auto-unfurls a card
  // with the cert preview image.
  const xIntentUrl = (() => {
    const text = `Just earned my Oak AI Campus ${cert.mastery_level || "Mastery"} credential in ${cert.goal || ""}.`.slice(0, 200);
    const params = new URLSearchParams({
      text,
      url: shareUrl,
      hashtags: "OakAICampusMastery,VerifiableCredential",
      via: "OakAICampus",
    });
    return `https://x.com/intent/tweet?${params.toString()}`;
  })();

  return (
    <div className="min-h-screen pb-20 md:pb-0" data-testid="certificate-page">
      <Header credits={credits} onOpenAd={() => {}} onOpenCheckout={() => {}} />
      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 space-y-6">
        <button onClick={() => navigate(-1)} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-2">
          <ArrowLeft size={14} /> {t("cert_back")}
        </button>

        <div>
          <div className="label-eyebrow mb-2 flex items-center gap-2">
            <Trophy size={12} className="text-[#ffb067]" /> {t("cert_eyebrow")}
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight">{t("cert_title")}</h1>
          <p className="text-white/55 text-sm max-w-xl mt-1">
            {t("cert_sub")}
          </p>
        </div>

        {/* Cinematic Certificate */}
        <div
          ref={ref}
          className="relative rounded-3xl overflow-hidden border border-white/15"
          style={{
            background:
              "radial-gradient(900px circle at 15% 0%, rgba(255,123,0,0.25), transparent 55%), radial-gradient(700px circle at 100% 100%, rgba(93,63,211,0.35), transparent 55%), linear-gradient(180deg, #0B0E14 0%, #131720 100%)",
          }}
          data-testid="certificate-card"
        >
          <div className="p-8 md:p-14">
            <div className="flex items-center justify-between gap-3 mb-10">
              <div className="flex items-center gap-3">
                <OakLogo size={36} glow />
                <div>
                  <div className="font-display font-semibold text-white">Oak AI Campus</div>
                  <div className="label-eyebrow !text-[10px]">Universal Certificate of Mastery</div>
                </div>
              </div>
              <div className="text-[11px] text-white/45 font-mono-oak">{cert.cert_id}</div>
            </div>

            <div className="text-white/55 text-xs tracking-[0.3em] uppercase">{t("cert_label_name")}</div>
            <div className="font-display text-4xl md:text-6xl tracking-tight mt-2 text-white">{cert.name}</div>
            <div className="text-white/65 mt-5 max-w-2xl text-[15px] leading-relaxed">
              {t("cert_completed")}
            </div>
            <div
              className="font-display text-2xl md:text-3xl mt-2 bg-gradient-to-r from-[#00FF9D] via-[#0044FF] to-[#FF7B00] bg-clip-text text-transparent uppercase tracking-tight"
              style={{ WebkitBackgroundClip: "text" }}
              data-testid="cert-goal-uppercase"
            >
              “{(cert.goal || "").toUpperCase()}”
            </div>

            {/* B4 — Mastery Level badge (Foundation / Practitioner / Expert) */}
            {cert.mastery_level && (
              <div className="mt-5 inline-flex items-center gap-2 rounded-full border border-[#ffb067]/40 bg-[#ffb067]/[0.08] px-4 py-1.5">
                <Award size={14} className="text-[#ffb067]" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#ffb067]/85">Mastery Level</span>
                <span className="font-display font-semibold text-[#ffb067] text-sm">{cert.mastery_level}</span>
              </div>
            )}

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label={t("cert_equity")} value={`${cert.equity}%`} />
              <Stat label={t("cert_modules")} value={`${cert.breakdown.modules_complete}`} />
              <Stat label={t("cert_exams")} value={`${cert.breakdown.exams_taken}`} />
              {/* Score % replaces the old Saved-PDFs tile (institutional pivot —
                  PDFs no longer count toward Knowledge Equity). */}
              <Stat label="Score %" value={`${cert.breakdown.exam_avg_score || 0}%`} testid="cert-stat-score" />
            </div>

            <div className="mt-12 flex items-end justify-between gap-5 flex-wrap">
              <div>
                <div className="text-[11px] text-white/50">{t("cert_issued")}</div>
                <div className="font-mono-oak text-sm text-white/80">
                  {new Date(cert.issued_at).toLocaleDateString(lang || undefined, { month: "long", day: "numeric", year: "numeric" })}
                </div>
              </div>
              {/* QR code is the central verification anchor for Open Badges 2.0
                  — scanning it lands on the public verify page which 302s
                  to the OB 2.0 JSON-LD assertion. */}
              {qrDataUrl && (
                <div className="flex flex-col items-center gap-1" data-testid="cert-qr">
                  <img
                    src={qrDataUrl}
                    alt={`Verify ${cert.cert_id}`}
                    className="w-[88px] h-[88px] rounded-md border border-white/15 bg-black/40 p-1"
                    crossOrigin="anonymous"
                  />
                  <div className="text-[8px] text-white/40 tracking-[0.25em] uppercase font-mono-oak">
                    Scan to verify
                  </div>
                </div>
              )}
              <div className="text-right">
                {/* Institutional issuer line replaces personal signature. */}
                <div className="font-display italic text-xl text-white/85" data-testid="cert-issuer">
                  Issued by Oak AI Campus
                </div>
                <div className="text-[11px] text-white/45">
                  Digiluxcon Technology Services (OPC) Pvt. Ltd.
                </div>
              </div>
            </div>

            {/* Validation Bar — bottom of the cinematic certificate.
                Signals the infrastructure used for path generation +
                7pt trademark disclaimer. */}
            <div
              className="mt-10 pt-5 border-t border-white/8 flex flex-col items-center gap-2.5"
              data-testid="cert-validation-bar"
            >
              <InfraBadges compact />
              <div className="text-[10px] text-white/45 font-mono-oak tracking-[0.15em] uppercase">
                <a
                  href="/issuer"
                  className="hover:text-[#9bffcc] transition"
                  data-testid="cert-verify-issuer-link"
                >
                  Verify issuer profile →
                </a>
              </div>
              <p
                className="text-white/35 leading-snug text-center max-w-3xl font-mono-oak"
                style={{ fontSize: "7pt" }}
              >
                {INFRA_DISCLAIMER}
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3">
          <button
            onClick={exportPng}
            disabled={exporting}
            className="btn-primary rounded-xl px-4 py-2.5 text-sm font-display inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="btn-cinematic-export"
          >
            {exporting ? t("cert_image_rendering") : (<><ImageIcon size={14} /> {t("cert_image_btn")}</>)}
          </button>
          {/* LinkedIn "Add to Profile" — pre-fills the LinkedIn Add
              Certification form with this cert's metadata. Opens in a
              new tab. Free, no partner status, viral by design. */}
          <a
            href={linkedInAddUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl px-4 py-2.5 text-sm font-display inline-flex items-center gap-2 border border-[#0A66C2]/50 bg-[#0A66C2]/[0.12] text-[#7eb8f0] hover:bg-[#0A66C2]/[0.22] transition"
            data-testid="btn-add-to-linkedin"
            title="Add this certificate to your LinkedIn profile"
          >
            <Linkedin size={14} /> Add to LinkedIn
          </a>
          <a
            href={xIntentUrl}
            target="_blank"
            rel="noreferrer"
            className="rounded-xl px-4 py-2.5 text-sm font-display inline-flex items-center gap-2 border border-white/25 bg-white/[0.04] text-white/85 hover:bg-white/[0.10] transition"
            data-testid="btn-share-on-x"
            title="Share this credential on X (Twitter) — auto-unfurls a card with the cert preview"
          >
            <Twitter size={14} /> Share on X
          </a>
          <button
            onClick={copyVerifyLink}
            className="rounded-xl px-4 py-2.5 text-sm font-display inline-flex items-center gap-2 border border-[#00FF9D]/40 bg-[#00FF9D]/[0.08] text-[#00FF9D] hover:bg-[#00FF9D]/[0.16] transition"
            data-testid="btn-copy-verify-link"
            title={shareUrl}
          >
            <CheckCircle2 size={14} /> Copy verifiable URL
            <Copy size={12} className="opacity-60" />
          </button>
          <a
            href={`${api.defaults.baseURL}/certificate/${pathId}`}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1"
          >
            <Download size={12} /> {t("cert_json")}
          </a>
        </div>

        <ShareBar url={shareUrl} title="Oak AI Campus certificate" text={shareText} />
      </main>
      <Footer />
      <BottomNav />
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="bg-black/30 border border-white/10 rounded-xl p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-white/45">{label}</div>
      <div className="font-display text-xl text-white">{value}</div>
    </div>
  );
}
